import { useState, useEffect } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import { auth, signInWithGoogle, signInWithEmail, signOutUser, onAuthStateChange } from '../Services/firebase';

interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

interface UseFirebaseAuthReturn {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  loginWithGoogle: () => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

export const useFirebaseAuth = (): UseFirebaseAuthReturn => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Converter Firebase User para nosso formato
  const convertFirebaseUser = (firebaseUser: FirebaseUser | null): User | null => {
    if (!firebaseUser) return null;
    
    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      photoURL: firebaseUser.photoURL
    };
  };

  useEffect(() => {
    // Observar mudanças de autenticação
    const unsubscribe = onAuthStateChange((firebaseUser) => {
      const user = convertFirebaseUser(firebaseUser);
      
      if (user) {
        setUser(user);
        setError(null);
      } else {
        setUser(null);
        setError(null);
      }
      
      setIsLoading(false);
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, []);

  const loginWithEmail = async (email: string, password: string): Promise<void> => {
    try {
      setError(null);
      setIsLoading(true);
      await signInWithEmail(email, password);
      // O estado será atualizado automaticamente pelo onAuthStateChange
    } catch (error: any) {
      console.error('Erro no login com email:', error);
      
      // Tratar erros específicos do Firebase
      if (error.code === 'auth/user-not-found') {
        setError('Usuário não encontrado. Verifique o email.');
      } else if (error.code === 'auth/wrong-password') {
        setError('Senha incorreta. Tente novamente.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Email inválido. Verifique o formato.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Muitas tentativas. Tente novamente em alguns minutos.');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Erro de conexão. Verifique sua internet.');
      } else {
        setError('Erro ao fazer login. Verifique suas credenciais.');
      }
      setIsLoading(false);
    }
  };

  const loginWithGoogle = async (): Promise<void> => {
    try {
      setError(null);
      setIsLoading(true);
      await signInWithGoogle();
      // O estado será atualizado automaticamente pelo onAuthStateChange
    } catch (error: any) {
      console.error('Erro no login com Google:', error);
      
      // Tratar erros específicos do Firebase
      if (error.code === 'auth/popup-closed-by-user') {
        setError('Login cancelado pelo usuário');
      } else if (error.code === 'auth/popup-blocked') {
        setError('Popup bloqueado pelo navegador. Permita popups para este site.');
      } else if (error.code === 'auth/unauthorized-domain') {
        setError('Domínio não autorizado. Entre em contato com o administrador.');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Erro de conexão. Verifique sua internet e tente novamente.');
      } else {
        setError('Erro ao fazer login com Google. Tente novamente.');
      }
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setError(null);
      await signOutUser();
      // O estado será atualizado automaticamente pelo onAuthStateChange
    } catch (error: any) {
      console.error('Erro no logout:', error);
      setError('Erro ao fazer logout. Tente novamente.');
    }
  };

  const clearError = (): void => {
    setError(null);
  };

  return {
    user,
    isLoading,
    error,
    loginWithGoogle,
    loginWithEmail,
    logout,
    clearError
  };
}; 